import React, { useMemo } from "react";
import { LoadableObject } from "../objects";
import SceneObject from "../objects/SceneObject";
import InstancedObjectGroup from "../objects/InstancedObjectGroup";
import { PHYSICS_TYPES, USE_INSTANCED_RENDERING } from "../../config/game";
import { MapLoadingTrackerItems } from "../../types/MapData.types";
import type { MapData } from "../../types/MapData.types";

interface MapObjectsProps {
  terrain: MapData["terrain"];
  category: string;
  mapId: string;
}

export default function MapObjects({ terrain, category, mapId }: MapObjectsProps) {
  const layoutTextureUrl = category ? `/objects/_Textures/${category}.png` : undefined;
  const groundY = (terrain.ground.position[1] ?? -3.5) + (terrain.ground.size[1] ?? 5) / 2;

  const objectGroups = useMemo(() => {
    const groups = new Map<string, typeof terrain.objects>();
    for (const obj of terrain.objects) {
      const existing = groups.get(obj.modelURL) || [];
      existing.push(obj);
      groups.set(obj.modelURL, existing);
    }
    return Array.from(groups.entries());
  }, [terrain.objects]);

  if (USE_INSTANCED_RENDERING) {
    return (
      <>
        {objectGroups.map(([modelURL, objects]) => (
          <InstancedObjectGroup
            key={modelURL}
            modelURL={modelURL}
            objects={objects}
            mapId={mapId}
            layoutTextureUrl={layoutTextureUrl}
            groundY={groundY}
            physicsTypes={PHYSICS_TYPES}
          />
        ))}
      </>
    );
  }

  return (
    <>
      {terrain.objects.map((object) => {
        const isPhysicalObject = PHYSICS_TYPES.includes(object.type.toLowerCase());
        return (
          <LoadableObject
            key={object.id}
            modelURL={object.modelURL}
            mapId={mapId}
            itemId={object.id}
            itemType={MapLoadingTrackerItems.OBJECT}
            isShowInLoader={true}
          >
            {(gltf) => (
              <SceneObject
                object={{ ...object, gltf }}
                isPhysicalObject={isPhysicalObject}
                layoutTextureUrl={layoutTextureUrl}
                groundY={groundY}
              />
            )}
          </LoadableObject>
        );
      })}
    </>
  );
}
